import { identity, pipe, ReadonlyArray } from "fp";

import moment from "moment";

import { Asset } from "~/Asset";
import * as Data from "~/Data";
import * as Driver from "~/Driver";
import { Order } from "~/Order";
import { Route as GenericRoute } from "~/Route";
import { Search as GenericSearch } from "~/Search";
import { useSelector } from "~/State";
import { Time } from "~/Time";
import { WebAPI } from "~/WebAPI";

import * as Search from "./Search";
import * as Sort from "./Sort";
import * as Submission from "./Submission";

export * as Action from "./Action";
export * as Count from "./Count";
export * as Download from "./Download";
export * as Interval from "./Interval";
export * as Page from "./Page";
export * as Request from "./Request";
export * as Search from "./Search";
export * as Sort from "./Sort";
export * as Submission from "./Submission";
export * as Table from "./Table";

/**
 * A submission from either stack. A driver's history splits at their rollout
 * cutover, so one table shows both — and `assets`, `submitter` and `sentTo` are
 * populated only for submissions made on the legacy stack until SC-559295
 * records them on the elogs side.
 */
export type ELD = Data.Reference.Reference &
  Time.Interval & {
    readonly driver: Driver.Driver;
    readonly submission: Submission.Submission;
    readonly sentTo?: string;
    readonly reference?: string;
    readonly assets: readonly Asset[];
  };

export const { compare } = Order.fromCompare<ELD>((a, b) =>
  Time.DateTime.compare(a.submission, b.submission)
);

export const useStore = (): Data.Store.Store<ELD> =>
  Data.Store.useSelector(({ elds: { store } }) => store);

export const useAllSorted = (): readonly ELD[] =>
  pipe(Sort.useActive(), (sort) =>
    pipe(
      useStore(),
      Data.Store.toCache,
      Data.Cache.toList((_id, eld) => eld),
      ReadonlyArray.sort(Sort.Type.toOrdering(sort.type)),
      sort.direction === Sort.Direction.descending()
        ? ReadonlyArray.reverse
        : identity
    )
  );

/**
 * Everything a row can be searched by, taken from the model rather than from the
 * text the row happens to render. Reading it back out of the DOM was what forced
 * the old filter to hide non-matching rows in place instead of removing them —
 * it could not know whether a row matched until after it had rendered it.
 */
export const toSearch: GenericSearch.From<ELD> = (eld) =>
  GenericSearch.join(
    Driver.toSearch(eld.driver),
    GenericSearch.string(eld.submission.submitter.name),
    GenericSearch.string(Submission.Status.toString(eld.submission.status)),
    Time.DateTime.toSearch(eld.submission),
    Time.DateTime.toSearch(eld.interval.start),
    Time.Interval.isStopped(eld)
      ? Time.DateTime.toSearch(eld.interval.stop)
      : GenericSearch.empty(),
    eld.sentTo ? GenericSearch.string(eld.sentTo) : GenericSearch.empty(),
    eld.reference ? GenericSearch.string(eld.reference) : GenericSearch.empty(),
    ...eld.assets.map(Asset.toSearch)
  );

/** The rows to render: sorted, then narrowed to those matching the search. */
export const useAllVisible = (): readonly ELD[] =>
  pipe(Search.useSearch(), (search) =>
    pipe(
      useAllSorted(),
      ReadonlyArray.filter((eld) =>
        pipe(
          eld,
          toSearch,
          GenericSearch.matches(GenericSearch.Match.string(search))
        )
      )
    )
  );

export const useInterval = (): Time.Interval.Stopped =>
  useSelector(({ elds }) => elds);

export namespace Route {
  export const definition = () => "/submissions" as const;
  export const href = () => definition();
  export const use = () => GenericRoute.use(href());
}

/**
 * How far back submissions are kept, and so how far back this page can show
 * anything. Asking for an earlier start returns a window we have no data for,
 * which reads as a bug rather than as a retention limit — the range control
 * clamps to it instead.
 */
export const retentionMonths = (): number => 6;

/** The earliest start the range control accepts: today less the retention period. */
export const earliestStart = (): Time.DateTime =>
  Time.DateTime.fromNumber(
    moment().subtract(retentionMonths(), "months").startOf("day").valueOf()
  );

export namespace State {
  export type State = Time.Interval.Stopped & {
    readonly search: Search.Search;
    readonly sort: Sort.State.State;
    readonly store: Data.Store.Store<ELD>;
  };

  export const initial = (): State => ({
    search: Search.State.initial(),
    sort: Sort.State.initial(),
    store: Data.Store.initial(),
    interval: {
      start: earliestStart(),
      stop: Time.DateTime.fromNumber(moment().endOf("day").valueOf()),
    },
  });
}

export const fromWebAPI = (submission: WebAPI.ELD.SubmissionFragment): ELD => ({
  id: submission.id,
  driver: {
    id: `${submission.driverId}`,
    name: { display: submission.driverName ?? undefined },
  },

  interval: {
    start: Time.DateTime.fromISOUnsafe(submission.startDate),
    stop: Time.DateTime.fromISOUnsafe(submission.endDate),
  },

  submission: Submission.fromWebAPI(submission),
  assets: submission.assets.map(({ assetId, name }) => ({
    asset: { id: `${assetId}`, name: name ?? undefined },
  })),
  sentTo: submission.sentToEmail ?? undefined,
  reference: submission.outputFileComment ?? undefined,
});
